import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const PAGE_SIZE = 12;

const formatDiamonds = (n) => {
  if (!n && n !== 0) return '—';
  return n?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const fmtUSD = (n) => {
  if (!n && n !== 0) return '—';
  return '$' + Math.round(n)?.toLocaleString('fr-FR');
};

// FIX C3: Format decimal hours as hours + minutes
const formatHours = (decimal) => {
  if (!decimal && decimal !== 0) return '—';
  const val = parseFloat(decimal);
  if (isNaN(val)) return '—';
  const h = Math.floor(val);
  const m = Math.round((val - h) * 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 border text-xs" style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a' }}>
      <p className="text-white/50 mb-1">{label}</p>
      <p className="font-bold text-white">💎 {formatDiamonds(payload?.[0]?.value)}</p>
    </div>
  );
};

const MonthlyHistory = ({ monthlyData, loading }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('period');
  const [sortDir, setSortDir] = useState('desc');

  // Last 12 months for chart
  const chartData = useMemo(() => {
    return [...(monthlyData || [])]
      ?.sort((a, b) => (a?.period || '')?.localeCompare(b?.period || ''))
      ?.slice(-12)
      ?.map((r) => ({
        period: r?.period?.slice(0, 7) || '',
        diamonds: r?.diamonds || 0,
        tier: r?.tier_p != null ? `T${r?.tier_p}` : '',
      }));
  }, [monthlyData]);

  const sortedData = useMemo(() => {
    return [...(monthlyData || [])]?.sort((a, b) => {
      const aVal = a?.[sortField] ?? '';
      const bVal = b?.[sortField] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortDir === 'asc'
        ? String(aVal)?.localeCompare(String(bVal))
        : String(bVal)?.localeCompare(String(aVal));
    });
  }, [monthlyData, sortField, sortDir]);

  const totalPages = Math.ceil(sortedData?.length / PAGE_SIZE);
  const pageData = sortedData?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  const columns = [
    { key: 'period', label: t('admin.creators.periodCol', 'Période') },
    { key: 'diamonds', label: t('admin.creators.diamonds', 'Diamonds') },
    { key: 'live_days', label: t('admin.creators.liveDays', 'Jours live') },
    { key: 'live_hours', label: t('admin.creators.hoursLabel', 'Heures') },
    { key: 'tier_p', label: t('admin.creators.tierLabel', 'Tier') },
    { key: 'activeness_level', label: t('admin.creators.activenessLabel', 'Activeness') },
    { key: 'total_bonus', label: t('admin.creators.totalBonusCol', 'Bonus Total') },
    { key: 'charge', label: 'Charge' },
    { key: 'cashback', label: t('admin.creators.cashbackLabel', 'Cashback') },
    { key: 'net_agency', label: t('admin.creators.netAgency', 'Net Agence') },
  ];

  return (
    <div>
      <h2 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Sora, sans-serif' }}>
        {t('admin.creators.monthlyHistory', 'Historique Mensuel')}
      </h2>
      {/* Bar Chart */}
      <div className="rounded-xl border p-4 mb-6" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
        <p className="text-xs uppercase tracking-wider mb-4" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Sora, sans-serif' }}>
          {t('admin.creators.diamondsPerMonth', 'Diamonds par mois (12 derniers mois)')}
        </p>
        {loading ? (
          <div className="h-48 rounded animate-pulse" style={{ backgroundColor: '#1a1a1a' }} />
        ) : chartData?.length === 0 ? (
          <div className="h-48 flex items-center justify-center" style={{ color: 'rgba(255,255,255,0.25)' }}>
            {t('admin.creators.noDataAvailable', 'Aucune donnée disponible')}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
              <XAxis dataKey="period" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v / 1000)?.toFixed(0)}k` : v} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="diamonds" radius={[3, 3, 0, 0]}>
                {chartData?.map((entry, i) => (
                  <Cell key={i} fill="#E5202A" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      {/* Table */}
      <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                {columns?.map((col) => (
                  <th
                    key={col?.key}
                    className="px-4 py-3 text-left font-semibold uppercase tracking-wider cursor-pointer hover:text-white select-none"
                    style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Sora, sans-serif', backgroundColor: '#111111' }}
                    onClick={() => handleSort(col?.key)}
                  >
                    <span className="flex items-center gap-1">
                      {col?.label}
                      {sortField === col?.key && (
                        <span style={{ color: '#E5202A' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)]?.map((_, i) => (
                  <tr key={i}>
                    {columns?.map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-3 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f', width: '60px' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : pageData?.length === 0 ? (
                <tr>
                  <td colSpan={columns?.length} className="px-4 py-10 text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    {t('admin.creators.noDataAvailable', 'Aucune donnée disponible')}
                  </td>
                </tr>
              ) : (
                pageData?.map((row, i) => (
                  <tr key={i} className="border-t transition-colors" style={{ borderColor: '#1f1f1f', backgroundColor: row?.is_estimated ? 'rgba(249,115,22,0.04)' : 'transparent' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = row?.is_estimated ? 'rgba(249,115,22,0.08)' : '#1a1a1a'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = row?.is_estimated ? 'rgba(249,115,22,0.04)' : 'transparent'; }}
                  >
                    <td className="px-4 py-3 font-medium text-white/70">
                      <span className="flex items-center gap-2">
                        {row?.period || '—'}
                        {row?.is_estimated && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: 'rgba(249,115,22,0.12)', color: '#f97316', border: '1px solid rgba(249,115,22,0.25)' }}>
                            {t('admin.creators.estimated', 'Estimé')}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white font-semibold">💎 {formatDiamonds(row?.diamonds)}</td>
                    <td className="px-4 py-3 text-white/60">{row?.live_days ?? '—'}</td>
                    {/* FIX C3: Format live_hours as hours + minutes */}
                    <td className="px-4 py-3 text-white/60">{formatHours(row?.live_hours)}</td>
                    <td className="px-4 py-3">
                      {row?.tier_p != null ? (
                        <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: 'rgba(107,114,128,0.15)', color: '#9ca3af', border: '1px solid rgba(107,114,128,0.3)' }}>
                          T{row?.tier_p}
                        </span>
                      ) : '—'}
                    </td>
                    {/* FIX C4: Display activeness_level as L{n} badge or dash */}
                    <td className="px-4 py-3">
                      {row?.activeness_level != null && row?.activeness_level > 0 ? (
                        <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.25)', fontFamily: 'Sora, sans-serif' }}>
                          L{row?.activeness_level}
                        </span>
                      ) : (
                        <span style={{ color: 'rgba(255,255,255,0.25)' }}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white/70">{row?.total_bonus != null ? fmtUSD(row?.total_bonus) : '—'}</td>
                    <td className="px-4 py-3 text-white/70">{row?.charge != null ? fmtUSD(row?.charge) : '—'}</td>
                    <td className="px-4 py-3 text-white/70">{row?.cashback != null ? fmtUSD(row?.cashback) : '—'}</td>
                    <td className="px-4 py-3 text-white/70">{row?.net_agency != null ? fmtUSD(row?.net_agency) : '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: '#1f1f1f' }}>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
              {t('admin.creators.pageInfo', 'Page {{page}} / {{total}} — {{count}} entrées', { page, total: totalPages, count: sortedData?.length })}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded text-xs border transition-colors disabled:opacity-30"
                style={{ borderColor: '#1f1f1f', color: 'rgba(255,255,255,0.6)', backgroundColor: 'transparent' }}
              >
                ←
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 rounded text-xs border transition-colors disabled:opacity-30"
                style={{ borderColor: '#1f1f1f', color: 'rgba(255,255,255,0.6)', backgroundColor: 'transparent' }}
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyHistory;
